import { api } from '@/api';
import { RootState } from '@/store/index';
import { Template } from '@/types/Template';
import { Video } from '@/types/Video';
import { ActionContext, Module } from 'vuex';

export type VideosState = {
    videos: Video[];
};

export function mapResponseToVideo(video: Partial<Video> & Pick<Video, 'id' | 'template'>, template?: Template): Video {
    return {
        id: video.id,
        template: {
            id: template?.id || video.template.id,
            name: template?.name || video.template.name,
            preview_image: template?.preview_image || video.template.preview_image,
        },
        uid: video.uid,
        state: video.state || 'new',
        url: video.url || undefined,
        preview_image: video.preview_image || undefined,
        created_at: video.created_at || undefined,
    };
}

export const videos: Module<VideosState, RootState> = {
    state: {
        videos: [],
    },
    mutations: {
        addVideo(state: VideosState, video: Video) {
            const index = state.videos.findIndex((item) => item.id === video.id);

            if (index === -1) {
                state.videos.push(video);
            } else {
                state.videos.splice(index, 1, video);
            }
        },
        removeVideo(state: VideosState, video: Video) {
            const index = state.videos.findIndex((item) => item.id === video.id);

            if (index !== -1) {
                state.videos.splice(index, 1);
            }
        },
    },
    getters: {
        videos: (state): Video[] => state.videos,
        unfinishedVideos: (state): Video[] => state.videos.filter((video) => video.state !== 'finished'),
        finishedVideos: (state): Video[] => state.videos.filter((video) => video.state === 'finished'),
    },
    actions: {
        async createVideo(context: ActionContext<VideosState, RootState>, payload: { template: Template }) {
            const response = await api.post('/videos', {
                template_id: payload.template.id,
                version: payload.template.version,
            });

            if (!response || !response.data) {
                throw new Error('Could not create video');
            }

            const video = mapResponseToVideo(response.data.data, payload.template);
            context.commit('addVideo', video);

            return video;
        },
        async fetchVideo(context: ActionContext<VideosState, RootState>, video: Video) {
            const response = await api.get(`/videos/${video.id}`);

            if (!response || !response.data) {
                throw new Error('Could not load video');
            }

            const fetchedVideo = mapResponseToVideo(response.data.data);
            context.commit('addVideo', fetchedVideo);

            return fetchedVideo;
        },
        async deleteVideo(context: ActionContext<VideosState, RootState>, payload: { video: Video, remote: boolean, local: boolean }) {
            const { video, local, remote } = payload;

            if (remote) {
                await api.delete(`/videos/${video.id}`);
            }

            if (local) {
                context.commit('removeVideo', video);
            }
        },
    },
};
